import { useSelector } from 'react-redux';
import { ProductImageTag, ProductImageTagType } from '../redux/productdetail/types';
import StateInterface from '../redux/types';
import { ImageResizeConfig } from '../components/Product/productImageResizeConfigs';
import useConfig from './useConfig';
import { reformatImage } from './useImageFormatter';

const addedPaddingTags: ProductImageTagType[] = [ProductImageTag.REGULAR, ProductImageTag.INFO_GRAPHIC];

const excludeTrimmingTags: Set<ProductImageTagType> = new Set([ProductImageTag.DETAIL, ProductImageTag.SIZE_RELATION]);

interface ImageTypeSource {
    webpSrc: string;
    jpegSrc: string;
}

export enum ImageWidths {
    PLP_DESKTOP_TABLET_LOW_DENSITY = 375,
    PLP_DESKTOP_TABLET_HIGH_DENSITY = 750,
    PLP_DESKTOP_WIDE_SCREEN_LOW_DENSITY = 465,
    PLP_DESKTOP_WIDE_SCREEN_HIGH_DENSITY = 930,
    PLP_MOBILE_LOW_DENSITY = 228,
    PLP_MOBILE_HIGH_DENSITY = 456,
    PLP_IPHONE_X = 375,

    PLP_HERO_IPHONE_X = 355,
    PLP_HERO_MOBILE_LOW_DENSITY = 460,
    PLP_HERO_MOBILE_HIGH_DENSITY = 920,

    PDP_SLIDER_IPHONE_X = 375,
    PDP_SLIDER_MOBILE_LOW_DENSITY = 480,
    PDP_SLIDER_MOBILE_HIGH_DENSITY = 960,
    PDP_SLIDER_DESKTOP_LOW_DENSITY = 535,
    PDP_SLIDER_DESKTOP_HIGH_DENSITY = 1070,
    PDP_SLIDER_DESKTOP_WIDE_SCREEN_LOW_DENSITY = 600,
    PDP_SLIDER_DESKTOP_WIDE_SCREEN_HIGH_DENSITY = 1200,
    PDP_SLIDER_DESKTOP_ULTRA_WIDE_SCREEN_LOW_DENSITY = 695,
    PDP_SLIDER_DESKTOP_ULTRA_WIDE_SCREEN_HIGH_DENSITY = 1390,
    PDP_THUMBNAIL_LOW_DENSITY = 104,
    PDP_THUMBNAIL_HIGH_DENSITY = 208,
    REVIEWS_LOW_DENSITY = 72,
    REVIEWS_HIGH_DENSITY = 144,
    COLOR_VARIANTS = 136,
    CART_IMAGE = 160,
}

export interface ImageSizeInfo {
    padding?: {
        top: number;
        right: number;
        bottom: number;
        left: number;
    };
    size: {
        width: string;
        height?: string;
    };
}

const getShortPath = (src: string): string => {
    const key = 'simple';
    return `${key}${src.split(key)[1] || ''}`;
};

export const getImageSizeInfo = (screenWidth: number): ImageSizeInfo => {
    // the formulas here are as they are defined in https://docs.google.com/spreadsheets/d/1t1OBa2UVJLN9XVG-qCPTYeCQVFCFO3ZDOrNGnFCHLMY/edit#gid=0
    // see also https://westwing.jira.com/wiki/spaces/DEV/pages/1780056178/Image+reformatting+project#Technical-details
    const screenHeight = Math.round((screenWidth * 4) / 3);
    const horizontalPadding = Math.round(screenWidth / 12.5);
    const verticalPadding = Math.round(screenHeight / 7.3529411765);

    return {
        padding: {
            top: verticalPadding,
            right: horizontalPadding,
            bottom: verticalPadding,
            left: horizontalPadding,
        },
        size: {
            width: screenWidth.toString(),
            height: screenHeight.toString(),
        },
    };
};

type FormattingOptions = {
    webP?: boolean;
    padding?: boolean;
};

export const useImageTagFormatter = (
    tag: ProductImageTagType,
    options: FormattingOptions = { webP: false, padding: true }
): ((src: string, imageWidth: ImageWidths, imageSizeInfo?: ImageSizeInfo) => ImageTypeSource) => {
    const isImageServiceProductImageEnabled = useSelector<StateInterface, boolean>(
        ({ configurations }) => configurations.isImageServiceProductImageEnabled
    );

    const { staticCmsHost } = useConfig();

    const { webP = false, padding = true } = options;
    const resizeConfigurations: ImageResizeConfig = {
        padding,
        removeText: isImageServiceProductImageEnabled,
        isTrimmed: !excludeTrimmingTags.has(tag),
    };

    const isImageWithPadding = addedPaddingTags.includes(tag);

    const formatImage = (src: string, imageWidth: ImageWidths, imageSizeInfo?: ImageSizeInfo): ImageTypeSource => {
        let path = src;
        if (path.startsWith('http')) {
            path = getShortPath(path);
        }

        const desiredImageWidth = imageWidth;
        const sizeInfo = imageSizeInfo || getImageSizeInfo(desiredImageWidth);
        const paddingSize = isImageWithPadding ? sizeInfo.padding : undefined;
        const imageDimension = { ...sizeInfo.size, paddingSize };

        const webpSrc = reformatImage(path, staticCmsHost, {
            ...resizeConfigurations,
            ...imageDimension,
            webP,
        });

        const jpegSrc = reformatImage(path, staticCmsHost, {
            ...resizeConfigurations,
            ...imageDimension,
        });

        return { webpSrc, jpegSrc };
    };

    return formatImage;
};

export const useBaseImagePath = () => {
    const { staticCmsHost } = useConfig();
    return (src: string) => reformatImage(src, staticCmsHost, {});
};
